import { useState, useCallback, memo } from 'react';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { OptionItem } from './OptionItem';
import { useSortableSensors } from '@/hooks';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { QuestionOption } from '@/types';

interface OptionListProps {
  options: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
  disabled?: boolean;
}

export const OptionList = memo(function OptionList({ options, onChange, disabled = false }: OptionListProps) {
  const { t } = useBuilderTranslation();
  const [nextTempId, setNextTempId] = useState(-1);
  const [newlyAddedId, setNewlyAddedId] = useState<number | null>(null);
  const sensors = useSortableSensors();

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex((opt) => opt.id === active.id);
      const newIndex = options.findIndex((opt) => opt.id === over.id);

      const reorderedOptions = arrayMove(options, oldIndex, newIndex).map(
        (opt, index) => ({
          ...opt,
          displayOrder: index + 1,
          code: String.fromCharCode(65 + index),
        })
      );

      onChange(reorderedOptions);
    }
  }, [options, onChange]);

  const handleAddOption = useCallback(() => {
    const newOption: QuestionOption = {
      id: nextTempId,
      questionId: 0,
      label: '',
      code: String.fromCharCode(65 + options.length),
      displayOrder: options.length + 1,
      isOtherOption: false,
      isActive: true,
      isDeleted: false,
      createdDate: new Date().toISOString(),
    };
    setNextTempId(nextTempId - 1);
    setNewlyAddedId(nextTempId); // Track the newly added option for auto-focus
    onChange([...options, newOption]);

    // Clear the newly added state after a brief moment (after focus has been set)
    setTimeout(() => setNewlyAddedId(null), 100);
  }, [nextTempId, options, onChange]);

  const handleOptionChange = useCallback((id: number, label: string) => {
    onChange(
      options.map((opt) => (opt.id === id ? { ...opt, label } : opt))
    );
  }, [options, onChange]);

  const handleDeleteOption = useCallback((id: number) => {
    const filtered = options.filter((opt) => opt.id !== id);
    // Reassign codes and display order
    const reordered = filtered.map((opt, index) => ({
      ...opt,
      displayOrder: index + 1,
      code: String.fromCharCode(65 + index),
    }));
    onChange(reordered);
  }, [options, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, maxWidth: '100%' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={options.map((opt) => opt.id)}
          strategy={verticalListSortingStrategy}
        >
          {options.map((option, index) => (
            <OptionItem
              key={option.id}
              option={option}
              index={index}
              onChange={handleOptionChange}
              onDelete={handleDeleteOption}
              disabled={disabled}
              autoFocus={option.id === newlyAddedId}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddOption}
        disabled={disabled}
        fullWidth
        sx={{ mt: 0.5 }}
      >
        {t('options.add')}
      </Button>
    </Box>
  );
});

export default OptionList;
